import { Badge } from "@design";
import { useTranslation } from "react-i18next";
import type { AiModelResponse } from "@/api";
import { DefinitionList } from "@/components/section/SectionBlocks";
import { formatDate, formatList, formatNumber } from "@/i18n/format";
import { CAPABILITY_KEYS, hostingOf, localDate, type ModelType, protocolOf } from "./modelOptions";

/** Read-only view of a model's configuration (phones). */
export function ModelSummary({ model }: Readonly<{ model: AiModelResponse }>) {
  const { t } = useTranslation("adminModels");
  const capabilities = CAPABILITY_KEYS.filter((key) => model[key]).map((key) => t(`capabilities.${key}.label`));
  const items = [
    { label: t("form.id"), value: model.id },
    { label: t("form.deploymentName"), value: model.deploymentName },
    {
      label: t("columns.enabled"),
      value: (
        <Badge variant={model.isEnabled ? "success" : "neutral"}>
          {t(model.isEnabled ? "list.enabled" : "list.disabled")}
        </Badge>
      ),
    },
    { label: t("form.hosting"), value: t(`hostings.${hostingOf(model)}.label`) },
    { label: t("form.apiProtocol"), value: t(`protocols.${protocolOf(model)}.label`) },
    { label: t("form.contextWindow"), value: formatNumber(model.contextWindow) },
    { label: t("form.maxOutputTokens"), value: formatNumber(model.maxOutputTokens) },
    { label: t("form.costInput"), value: formatNumber(model.costPer1kInput) },
    { label: t("form.costOutput"), value: formatNumber(model.costPer1kOutput) },
    { label: t("form.costTier"), value: model.costTier || t("list.none") },
    { label: t("form.modelType"), value: t(`modelTypes.${(model.modelType ?? "chat") as ModelType}`) },
    { label: t("form.capabilities.title"), value: capabilities.length ? formatList(capabilities) : t("list.none") },
  ];
  if (model.description) items.push({ label: t("form.description"), value: model.description });
  if (model.isDeprecated) {
    items.push({
      label: t("form.deprecation.sunsetDate"),
      value: model.sunsetDate ? formatDate(localDate(model.sunsetDate)) : t("list.none"),
    });
  }
  return <DefinitionList items={items} />;
}
